import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle } from "lucide-react";

interface Decision {
  id: string;
  created_at: string;
  task_class: string;
  provider_used: string;
  model_used: string;
  was_fallback: boolean;
  latency_ms: number;
  estimated_cost_usd: number;
  classifier_version: string | null;
  fallback_path: string | null;
  blocked_by_block_list: string[] | null;
}

export function DecisionsFeed() {
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const [taskFilter, setTaskFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["model-decisions-feed"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("model_decisions")
        .select("id, created_at, task_class, provider_used, model_used, was_fallback, latency_ms, estimated_cost_usd, provenance_json")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data || []).map((row: Record<string, unknown>): Decision => {
        const prov = (row.provenance_json as Record<string, unknown>) || {};
        return {
          id: row.id as string,
          created_at: row.created_at as string,
          task_class: row.task_class as string,
          provider_used: row.provider_used as string,
          model_used: row.model_used as string,
          was_fallback: row.was_fallback as boolean,
          latency_ms: row.latency_ms as number,
          estimated_cost_usd: row.estimated_cost_usd as number,
          classifier_version: (prov.classifier_version as string) ?? null,
          fallback_path: (prov.fallback_path as string) ?? null,
          blocked_by_block_list: (prov.blocked_by_block_list as string[]) ?? null,
        };
      });
    },
    refetchInterval: 30000,
  });

  // Compute gemini_leakage: provider=google in a class that should be blocked
  const { rows, leakageCount, providers, taskClasses } = useMemo(() => {
    const rows = data ?? [];
    const provs = new Set<string>();
    const tasks = new Set<string>();
    let leakage = 0;
    const BLOCKED_FOR_GOOGLE = new Set([
      "factual_critical", "price_quote", "policy_statement",
      "procedural", "ltm_dependent", "verifier", "date_time_statement",
    ]);
    for (const r of rows) {
      provs.add(r.provider_used);
      tasks.add(r.task_class);
      if (r.provider_used?.toLowerCase().includes("google") && BLOCKED_FOR_GOOGLE.has(r.task_class)) {
        leakage++;
      }
    }
    return { rows, leakageCount: leakage, providers: Array.from(provs).sort(), taskClasses: Array.from(tasks).sort() };
  }, [data]);

  const filtered = rows.filter((r) => {
    if (providerFilter !== "all" && r.provider_used !== providerFilter) return false;
    if (taskFilter !== "all" && r.task_class !== taskFilter) return false;
    if (search && !`${r.model_used} ${r.provider_used} ${r.task_class}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {leakageCount > 0 && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <div>
              <p className="font-semibold text-destructive">Gemini leakage detectado: {leakageCount} decisão(ões)</p>
              <p className="text-xs text-muted-foreground">Provider <code>google</code> foi usado em task_class que deveria estar bloqueado.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Decisões recentes ({filtered.length}/{rows.length})</CardTitle>
              <CardDescription>Últimas 200 chamadas roteadas pelo gateway. Atualiza a cada 30s.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`w-4 h-4 mr-1 ${isFetching ? "animate-spin" : ""}`} /> Atualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3">
            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos providers</SelectItem>
                {providers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={taskFilter} onValueChange={setTaskFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas task_classes</SelectItem>
                {taskClasses.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-[200px]"
            />
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : (
            <div className="space-y-1.5 max-h-[600px] overflow-auto">
              {filtered.map((d) => {
                const isLeakage = d.provider_used?.toLowerCase().includes("google") &&
                  ["factual_critical","price_quote","policy_statement","procedural","ltm_dependent","verifier","date_time_statement"].includes(d.task_class);
                return (
                  <div
                    key={d.id}
                    className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${
                      isLeakage ? "border-destructive bg-destructive/5" : "border-border"
                    }`}
                  >
                    <span className="text-muted-foreground font-mono w-[140px] shrink-0">
                      {new Date(d.created_at).toLocaleTimeString("pt-BR")}
                    </span>
                    <Badge variant="outline" className="shrink-0">{d.task_class}</Badge>
                    <Badge variant={d.was_fallback ? "secondary" : "default"} className="shrink-0">
                      {d.provider_used}
                    </Badge>
                    <span className="font-mono truncate flex-1">{d.model_used}</span>
                    <span className="font-mono text-muted-foreground shrink-0">{d.latency_ms}ms</span>
                    <span className="font-mono text-muted-foreground shrink-0">${d.estimated_cost_usd?.toFixed(5) ?? "0"}</span>
                    {d.classifier_version && (
                      <Badge variant="outline" className="text-[10px] shrink-0">{d.classifier_version}</Badge>
                    )}
                    {d.fallback_path && d.fallback_path !== "primary" && (
                      <Badge variant="secondary" className="text-[10px] shrink-0">{d.fallback_path}</Badge>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhuma decisão encontrada.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
